import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Calendar, MapPin, Trophy, Sun, Download, ArrowRight } from "lucide-react";

const KIND_META = {
  competition: { label: "Competition", icon: Trophy, color: "bg-[#FF9F1C]/15 text-[#B36906] border-[#FF9F1C]/30" },
  camp: { label: "Camp", icon: Sun, color: "bg-[#FDE74C]/40 text-[#7A5F00] border-[#FDE74C]/60" },
};

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }); } catch { return s; }
}

function DateBadge({ dateStr }) {
  if (!dateStr) return null;
  let d;
  try { d = new Date(dateStr + "T00:00:00"); } catch { return null; }
  const month = d.toLocaleDateString(undefined, { month: "short" }).toUpperCase();
  const day = d.getDate();
  const year = d.getFullYear();
  return (
    <div className="shrink-0 w-20 rounded-2xl bg-white border-2 border-[#FF9F1C] shadow-[0_6px_16px_rgb(255,159,28,0.15)] overflow-hidden text-center" data-testid="event-date-badge">
      <div className="bg-[#FF9F1C] text-white text-[10px] font-black uppercase tracking-widest py-1">{month}</div>
      <div className="font-display font-black text-3xl leading-none pt-2 text-[#1E1E24]">{day}</div>
      <div className="text-[10px] text-[#7A7D8C] pb-2 pt-1 font-semibold">{year}</div>
    </div>
  );
}

export default function Events() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [kindFilter, setKindFilter] = useState("all");

  const load = () => api.get("/events").then((r) => setRows(r.data || [])).catch(() => toast.error("Failed to load events"));
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => kindFilter === "all" || r.kind === kindFilter);
  const today = new Date().toISOString().slice(0, 10);

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`);
    toast.success("Deleted");
    load();
  };

  const downloadTemplate = async () => {
    try {
      const url = `${api.defaults.baseURL}/competitors/csv-template`;
      const resp = await fetch(url, { credentials: "include" });
      if (!resp.ok) throw new Error("failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "naptown_competitors_template.csv";
      a.click();
      toast.success("Template downloaded");
    } catch {
      toast.error("Download failed");
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tight">Events</h1>
          <p className="text-[#7A7D8C] mt-1 text-sm">Create competitions and camps. Click an event to manage its competitors and rounds.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="csv-template-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold hover:border-[#FF9F1C]">
            <Download className="w-4 h-4" /> Roster template
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-event-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)]">
            <Plus className="w-4 h-4" /> Add event
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[{ k: "all", label: "All" }, { k: "competition", label: "Competitions" }, { k: "camp", label: "Camps" }].map((t) => (
          <button key={t.k} onClick={() => setKindFilter(t.k)} data-testid={`event-filter-${t.k}`} className={`px-4 py-2 rounded-full text-sm font-semibold border ${kindFilter === t.k ? "bg-[#FF9F1C] text-white border-[#FF9F1C]" : "bg-white border-slate-200"}`}>{t.label}</button>
        ))}
      </div>

      <div className="grid gap-4" data-testid="events-list">
        {filtered.length === 0 && <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center text-[#7A7D8C]">No events yet.</div>}
        {filtered.map((e) => {
          const M = KIND_META[e.kind] || KIND_META.competition;
          const Icon = M.icon;
          const past = e.end_date < today;
          const isCompetition = e.kind === "competition";
          return (
            <div key={e.id} className="group bg-white rounded-2xl border border-slate-100 p-6 hover:border-[#FF9F1C] hover:shadow-[0_10px_30px_rgb(255,159,28,0.10)] transition-all" data-testid={`event-card-${e.id}`}>
              <div className="flex flex-wrap gap-5 items-start">
                <DateBadge dateStr={e.start_date} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${M.color}`}><Icon className="w-3 h-3" /> {M.label}</span>
                    {e.visibility === "invite_only" && <span className="text-[10px] uppercase tracking-wider font-bold text-[#B36906] px-2 py-0.5 border border-[#FDE74C] bg-[#FDE74C]/40 rounded-full" data-testid={`mock-badge-${e.id}`}>Mock · Invite only</span>}
                    {!e.active && <span className="text-[10px] uppercase tracking-wider font-bold text-[#7A7D8C] px-2 py-0.5 border border-slate-200 rounded-full">Hidden</span>}
                    {past && <span className="text-[10px] uppercase tracking-wider font-bold text-[#E71D36] px-2 py-0.5 border border-red-200 rounded-full">Past</span>}
                  </div>
                  {isCompetition ? (
                    <Link to={`/admin/events/${e.id}`} data-testid={`event-open-${e.id}`} className="mt-2 inline-block font-display font-black text-xl hover:text-[#FF9F1C] transition-colors">
                      {e.title}
                    </Link>
                  ) : (
                    <h2 className="mt-2 font-display font-black text-xl">{e.title}</h2>
                  )}
                  <div className="mt-2 flex flex-wrap gap-4 text-xs text-[#7A7D8C]">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {fmtDate(e.start_date)}{e.end_date && e.end_date !== e.start_date ? ` – ${fmtDate(e.end_date)}` : ""}</span>
                    {(e.start_time || e.end_time) && <span>{e.start_time}{e.end_time ? ` – ${e.end_time}` : ""}</span>}
                    {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>}
                    {e.registration_fee != null && <span>${e.registration_fee}</span>}
                  </div>
                  {e.description && <p className="mt-3 text-sm text-[#1E1E24] whitespace-pre-wrap leading-relaxed">{e.description}</p>}
                  {isCompetition && (
                    <Link to={`/admin/events/${e.id}`} data-testid={`event-manage-${e.id}`} className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#FF9F1C] hover:gap-2 transition-all">
                      Manage competitors & rounds <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setEditing(e); setShowForm(true); }} data-testid={`event-edit-${e.id}`} className="p-2 rounded-lg hover:bg-slate-100"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(e.id)} data-testid={`event-delete-${e.id}`} className="p-2 rounded-lg hover:bg-red-50 text-[#E71D36]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <EventForm
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function EventForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState({
    kind: initial?.kind || "competition",
    title: initial?.title || "",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || "",
    location: initial?.location || "",
    start_time: initial?.start_time || "",
    end_time: initial?.end_time || "",
    description: initial?.description || "",
    is_free: initial?.is_free || initial?.registration_fee === 0,
    registration_fee: initial?.registration_fee ?? "",
    registration_url: initial?.registration_url || "",
    active: initial?.active ?? true,
    visibility: initial?.visibility || "public",
    send_notifications: initial?.send_notifications ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...f,
        end_date: f.end_date || f.start_date,
        registration_fee: f.is_free ? 0 : f.registration_fee === "" || f.registration_fee == null ? null : parseFloat(f.registration_fee),
      };
      if (initial) {
        await api.patch(`/events/${initial.id}`, payload);
        toast.success("Event updated");
      } else {
        await api.post("/events", payload);
        toast.success("Event created");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 grid place-items-center p-4 overflow-y-auto">
      <form onSubmit={save} className="bg-white rounded-3xl max-w-lg w-full p-8 relative my-8" data-testid="event-form">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 p-2 rounded-lg hover:bg-slate-100"><X className="w-4 h-4" /></button>
        <h2 className="font-display font-black text-2xl">{initial ? "Edit event" : "New event"}</h2>
        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Type</label>
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} data-testid="event-kind" className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
              <option value="competition">Competition</option>
              <option value="camp">Camp</option>
            </select>
          </div>
          <Field label="Title" value={f.title} required onChange={(v) => setF({ ...f, title: v })} tid="event-title" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date" type="date" value={f.start_date} required onChange={(v) => setF({ ...f, start_date: v })} tid="event-start-date" />
            <Field label="End date" type="date" value={f.end_date} onChange={(v) => setF({ ...f, end_date: v })} tid="event-end-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start time" type="time" value={f.start_time} onChange={(v) => setF({ ...f, start_time: v })} tid="event-start-time" />
            <Field label="End time" type="time" value={f.end_time} onChange={(v) => setF({ ...f, end_time: v })} tid="event-end-time" />
          </div>
          <Field label="Location" value={f.location} onChange={(v) => setF({ ...f, location: v })} tid="event-location" />
          {f.kind === "competition" && <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={f.is_free} onChange={(event) => setF({ ...f, is_free: event.target.checked, registration_fee: event.target.checked ? 0 : "" })} data-testid="event-free-competition" className="w-4 h-4 accent-[#FF9F1C]" />Free competition</label>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Registration fee ($)" type="number" value={f.registration_fee} disabled={f.is_free} onChange={(v) => setF({ ...f, registration_fee: v })} tid="event-fee" />
            <Field label="Registration URL" value={f.registration_url} onChange={(v) => setF({ ...f, registration_url: v })} tid="event-url" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Description</label>
            <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} data-testid="event-description" className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">Visibility</label>
            <div className="mt-1 grid grid-cols-2 gap-2" data-testid="event-visibility">
              <button type="button" onClick={() => setF({ ...f, visibility: "public" })} data-testid="visibility-public" className={`px-3 py-2.5 rounded-xl border text-sm font-semibold text-left ${f.visibility === "public" ? "border-[#FF9F1C] bg-[#FFBF69]/10 text-[#1E1E24]" : "border-slate-200 bg-white text-[#7A7D8C] hover:border-[#FFBF69]"}`}>
                <div>Public</div>
                <div className="text-[10px] font-normal opacity-80">Listed on the public site.</div>
              </button>
              <button type="button" onClick={() => setF({ ...f, visibility: "invite_only" })} data-testid="visibility-invite" className={`px-3 py-2.5 rounded-xl border text-sm font-semibold text-left ${f.visibility === "invite_only" ? "border-[#FF9F1C] bg-[#FFBF69]/10 text-[#1E1E24]" : "border-slate-200 bg-white text-[#7A7D8C] hover:border-[#FFBF69]"}`}>
                <div>Invite only <span className="inline-block ml-1 text-[9px] uppercase tracking-widest text-[#B36906] bg-[#FDE74C]/60 rounded-full px-1.5 py-0.5">Mock</span></div>
                <div className="text-[10px] font-normal opacity-80">Hidden. Access via emailed link.</div>
              </button>
            </div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="event-active" className="w-4 h-4 accent-[#FF9F1C]" /> {f.visibility === "invite_only" ? "Active (invitees can access)" : "Visible on public site"}
          </label>
          {f.visibility === "public" && (
            <div className="rounded-2xl border border-slate-200 bg-[#FDFFFC] p-4">
              <label className="inline-flex items-start gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={f.send_notifications} onChange={(e) => setF({ ...f, send_notifications: e.target.checked })} data-testid="event-send-notifications" className="w-4 h-4 mt-0.5 accent-[#FF9F1C]" />
                <span>
                  <span className="font-semibold">Send parent notifications</span>
                  <span className="block text-xs text-[#7A7D8C] mt-0.5">
                    {initial ? "Uncheck while drafting to suppress emails. Re-check later to instantly email opted-in parents." : "When on, opted-in parents get an email announcement as soon as this event is created."}
                  </span>
                </span>
              </label>
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-full border border-slate-200 text-sm font-semibold">Cancel</button>
          <button type="submit" disabled={saving} data-testid="event-save-btn" className="px-5 py-2 rounded-full bg-[#FF9F1C] text-white text-sm font-semibold shadow-[0_8px_20px_rgb(255,159,28,0.3)] disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, required, disabled = false, type = "text", tid }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-[#7A7D8C]">{label}</label>
      <input data-testid={tid} disabled={disabled} type={type} value={value ?? ""} required={required} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-xl border border-slate-200 bg-[#FDFFFC] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF9F1C]" />
    </div>
  );
}
